import { ReactNode, useEffect, useState } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";

import { criticalMuiEmotionStyles } from "./criticalMuiEmotionStyles";

type IframeStyleInjectorProps = {
    children: ReactNode;
    document: Document | undefined;
    onStylesLoaded?: () => void;
};

export const IframeStyleInjector = (props: IframeStyleInjectorProps) => {
    const { children, document: iFrameDoc, onStylesLoaded } = props;
    const [stylesLoaded, setStylesLoaded] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (!iFrameDoc || !iFrameDoc.head) return;

        const criticalStyles = iFrameDoc.createElement("style");
        criticalStyles.id = "critical-styles";
        criticalStyles.textContent = criticalMuiEmotionStyles;
        iFrameDoc.head.appendChild(criticalStyles);

        const allParentStyles = Array.from(document.styleSheets)
            .filter((sheet) => {
                try {
                    return sheet.cssRules !== null;
                } catch (e) {
                    return false;
                }
            })
            .map((sheet) => {
                try {
                    return Array.from(sheet.cssRules)
                        .map((rule) => rule.cssText)
                        .join("\n");
                } catch (e) {
                    return "";
                }
            })
            .join("\n");

        if (allParentStyles) {
            const parentStyles = iFrameDoc.createElement("style");
            parentStyles.id = "parent-styles";
            parentStyles.textContent = allParentStyles;
            iFrameDoc.head.appendChild(parentStyles);
        }

        const googleFontsLink = document.querySelector('link[href*="fonts.googleapis.com"]') as HTMLLinkElement;
        if (googleFontsLink && googleFontsLink.href) {
            const fontLink = iFrameDoc.createElement("link");
            fontLink.rel = "stylesheet";
            fontLink.href = googleFontsLink.href;
            iFrameDoc.head.appendChild(fontLink);
        }

        setStylesLoaded(true);
        if (onStylesLoaded) {
            onStylesLoaded();
        }
    }, [iFrameDoc, onStylesLoaded]);

    if (!stylesLoaded) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                }}
            >
                <CircularProgress size={40} />
                <Box sx={{ ml: theme.spacing(2) }}>Loading styles...</Box>
            </Box>
        );
    }

    return <>{children}</>;
};
